import { Link } from 'react-router';
import { type CSSProperties, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';

export type KpiSecondaryTone = 'neutral' | 'success' | 'warn' | 'danger' | 'info';

const TONE_DOT: Record<KpiSecondaryTone, string> = {
  neutral: 'var(--content-soft)',
  success: 'var(--status-success-fg)',
  warn: 'var(--status-warn-fg)',
  danger: 'var(--status-danger-fg)',
  info: 'var(--status-info-fg)',
};

export interface KpiSecondaryCardProps {
  label: string;
  value: string;
  tone?: KpiSecondaryTone;
  to?: string;
  hint?: string;
  testId: string;
  icon?: ReactNode;
}

export const KpiSecondaryCard = ({
  label,
  value,
  tone = 'neutral',
  to,
  hint,
  testId,
  icon,
}: KpiSecondaryCardProps) => {
  const interactive = Boolean(to);
  const cardStyle: CSSProperties = {
    background: 'var(--surface-raised)',
    border: '1px solid var(--divider-strong)',
    boxShadow: 'var(--shadow-card-rest)',
    transition: 'all 220ms cubic-bezier(0.16, 1, 0.3, 1)',
  };
  const body = (
    <article
      className="group relative flex h-full items-center gap-4 rounded-[var(--radius-card)] p-4 hover:-translate-y-[1px] hover:shadow-[var(--shadow-card-hover)]"
      style={cardStyle}
      data-tone={tone}
    >
      <span
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{ background: 'var(--surface-1)', color: TONE_DOT[tone] }}
      >
        {icon ?? <span className="h-2 w-2 rounded-full" style={{ background: TONE_DOT[tone] }} />}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-[11px] font-medium uppercase tracking-[0.06em]"
          style={{ color: 'var(--content-muted)' }}
        >
          {label}
        </p>
        <div className="flex items-baseline gap-2">
          <strong
            className="text-[18px] font-semibold leading-tight tabular-nums"
            style={{ color: 'var(--content-strong)' }}
            data-testid="dashboard-kpi-secondary-value"
          >
            {value}
          </strong>
          {hint ? (
            <span className="truncate text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {hint}
            </span>
          ) : null}
        </div>
      </div>
      {interactive ? (
        <ArrowRight
          aria-hidden="true"
          className="h-4 w-4 shrink-0 opacity-0 transition-all group-hover:opacity-100 group-hover:translate-x-1"
          style={{ color: 'var(--content-muted)' }}
        />
      ) : null}
    </article>
  );
  if (!interactive) {
    return <div data-testid={testId}>{body}</div>;
  }
  return (
    <Link
      to={to as string}
      data-testid={testId}
      className="block rounded-[var(--radius-card)] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
    >
      {body}
    </Link>
  );
};
